import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Code, Book, Terminal, Key, Menu, X } from 'lucide-react';
import './InfoPage.css';

const DocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="info-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <Code className="badge-icon" />
              <span>DOCUMENTATION</span>
            </div>
            <h1 className="hero-title">
              Developer <span className="gradient-text">Documentation</span>
            </h1>
            <p className="hero-subtitle">
              API documentation and integration guides for HomeQuest Tech platform.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="info-content">
        <div className="content-container">
          <h2>API Access</h2>
          <p>
            HomeQuest Tech offers a comprehensive REST API for integrating our AI-powered construction management features into your existing workflows and applications.
          </p>

          <h3>Beta Program API Access</h3>
          <p>
            API access is currently available to all beta program members. Join our founding member program to get immediate API access and help shape our developer platform.
          </p>

          <div style={{ padding: '2rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.2)', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <Key style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
              <h3 style={{ margin: 0 }}>API Features</h3>
            </div>
            <ul>
              <li>CAD File Processing API - Upload and process floor plans</li>
              <li>Site Analysis API - GIS integration and setback calculations</li>
              <li>Vendor Management API - Send bids and manage responses</li>
              <li>Project Management API - Create and manage projects</li>
              <li>Budget Tracking API - Invoice processing and analytics</li>
              <li>Webhooks - Real-time notifications for project updates</li>
            </ul>
          </div>

          <div style={{ padding: '2rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.2)', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <Terminal style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
              <h3 style={{ margin: 0 }}>Quick Start Example</h3>
            </div>
            <pre style={{ background: '#1F2937', padding: '1.5rem', borderRadius: '8px', overflow: 'auto', color: '#E5E7EB', fontSize: '0.875rem', lineHeight: '1.5' }}>
{`// Example API Request
curl -X POST https://api.homequest.tech/v1/cad/process \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file_url": "https://yourstorage.com/floorplan.dwg",
    "detect_rooms": true,
    "place_selections": true
  }'

// Response
{
  "status": "success",
  "rooms_detected": 12,
  "processing_time": "2.3s",
  "floor_plan_id": "fp_abc123xyz"
}`}
            </pre>
          </div>

          <div style={{ padding: '2rem', background: 'rgba(31, 41, 55, 0.6)', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.2)', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <Book style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
              <h3 style={{ margin: 0 }}>Documentation Sections</h3>
            </div>
            <ul>
              <li><strong>Authentication</strong> - API key management and OAuth 2.0</li>
              <li><strong>Endpoints</strong> - Complete API reference for all endpoints</li>
              <li><strong>SDKs</strong> - Official libraries for Python, JavaScript, and Ruby</li>
              <li><strong>Webhooks</strong> - Event subscriptions and payload formats</li>
              <li><strong>Rate Limits</strong> - Usage limits and best practices</li>
              <li><strong>Examples</strong> - Code samples and integration guides</li>
            </ul>
          </div>

          <h2>Getting API Access</h2>
          <p>
            To access the full API documentation and get your API keys:
          </p>
          <ol>
            <li>Join our beta program as a founding member</li>
            <li>Log in to your dashboard</li>
            <li>Navigate to Settings → API Keys</li>
            <li>Generate your API key and start integrating</li>
          </ol>

          <button onClick={() => navigate('/pricing')} className="cta-button">
            Join Beta & Get API Access
          </button>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/help')}>Help Center</a>
              <a onClick={() => navigate('/contact')}>Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocumentationPage;
